import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, RefreshCw, Phone, MailCheck, MailWarning, Smartphone, LogOut, Chrome } from "lucide-react";
import { SITE } from "../data/site";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/quote-notifications`;
const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

export default function Dashboard() {
  const location = useLocation();
  const [key, setKey] = useState(() => sessionStorage.getItem("nextg-dash-key") || "");
  const [input, setInput] = useState("");
  const [items, setItems] = useState(null);
  const [googleUser, setGoogleUser] = useState(location.state?.user || null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k, { silent = false } = {}) => {
    setLoading(true);
    if (!silent) setError("");
    try {
      const res = await axios.get(API, {
        headers: k ? { "X-Dashboard-Key": k } : {},
        withCredentials: true, // sends the Google session cookie when present
      });
      setItems(res.data.items);
      if (k) sessionStorage.setItem("nextg-dash-key", k);
    } catch {
      if (!silent) setError("Wrong passcode — try again.");
      setItems(null);
      if (k) {
        sessionStorage.removeItem("nextg-dash-key");
        setKey("");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    else load("", { silent: true }); // maybe a Google session cookie already exists
  }, [key, load]);

  // Verify who the Google session belongs to (for the sign-out button label).
  useEffect(() => {
    axios
      .get(`${AUTH_API}/me`, { withCredentials: true })
      .then((res) => setGoogleUser(res.data))
      .catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setKey(input.trim());
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const signOut = async () => {
    try {
      await axios.post(`${AUTH_API}/logout`, {}, { withCredentials: true });
    } catch {
      /* still clear locally */
    }
    setGoogleUser(null);
    setItems(null);
    sessionStorage.removeItem("nextg-dash-key");
    setKey("");
  };

  return (
    <section data-testid="dashboard-page" className="grain relative min-h-screen pt-28 sm:pt-36 pb-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <Reveal>
          <p className="eyebrow">Owner only</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
            Estimate <span className="text-volt">Dashboard</span>
          </h1>
          <p className="mt-4 max-w-xl text-ink/60">
            Every repair estimate visitors run on the website appears here, newest first.
          </p>
        </Reveal>

        {!items && (
          <Reveal delay={0.1} className="mt-12 max-w-md">
            <div className="glass-liquid rounded-[24px] p-7 sm:p-9">
              <button
                type="button"
                data-testid="google-sign-in-button"
                onClick={googleSignIn}
                className="flex w-full items-center justify-center gap-3 rounded-full bg-accent py-3.5 font-bold uppercase tracking-wide text-[#EDF1F7] transition-colors hover:bg-volt hover:text-[#0B111B]"
              >
                <Chrome className="h-4 w-4" /> Sign in with Google
              </button>
              <p className="mt-3 text-center text-[11px] text-ink/40">
                Owner access — the shop's Google account opens the dashboard.
              </p>
            </div>
            <form
              onSubmit={submit}
              data-testid="dashboard-login-form"
              className="glass-liquid mt-4 rounded-[24px] p-7 sm:p-9"
            >
              <label htmlFor="dash-key" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink/50">
                <Lock className="h-4 w-4 text-volt" /> Passcode
              </label>
              <input
                id="dash-key"
                data-testid="dashboard-passcode-input"
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter dashboard passcode"
                className="mt-4 w-full rounded-xl border border-ink/15 bg-white/5 px-4 py-3.5 text-ink placeholder:text-ink/35 focus:border-volt focus:outline-none"
              />
              {error && (
                <p data-testid="dashboard-error" className="mt-3 text-sm text-accent-bright">{error}</p>
              )}
              <button
                type="submit"
                data-testid="dashboard-login-button"
                disabled={loading}
                className="mt-5 w-full rounded-full bg-volt py-3.5 font-bold uppercase tracking-wide text-[#0B111B] transition-colors hover:bg-accent hover:text-[#EDF1F7] disabled:opacity-60"
              >
                {loading ? "Checking…" : "Open dashboard"}
              </button>
            </form>
          </Reveal>
        )}

        {items && (
          <>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
              <p data-testid="dashboard-count" className="font-mono text-xs uppercase tracking-[0.22em] text-ink/50">
                {items.length} estimate{items.length === 1 ? "" : "s"} recorded
                {googleUser && <span className="block text-ink/35 normal-case tracking-normal">signed in as {googleUser.email}</span>}
              </p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="dashboard-refresh-button"
                  onClick={() => load(key)}
                  className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors hover:border-volt hover:text-volt"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
                {googleUser && (
                  <button
                    type="button"
                    data-testid="dashboard-signout-button"
                    onClick={signOut}
                    className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors hover:border-accent hover:text-accent-bright"
                  >
                    <LogOut className="h-3.5 w-3.5" /> Sign out
                  </button>
                )}
              </div>
            </div>

            {items.length === 0 ? (
              <p data-testid="dashboard-empty" className="mt-16 text-center text-ink/50">
                No estimates yet — share the website and they will appear here.
              </p>
            ) : (
              <ul className="mt-8 space-y-4">
                {items.map((it) => (
                  <motion.li
                    key={it.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid={`dashboard-item-${it.id}`}
                    className="glass-liquid flex flex-col gap-4 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
                  >
                    <div className="flex items-start gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-ink/15 text-volt">
                        <Smartphone className="h-5 w-5" />
                      </span>
                      <div>
                        <p className="font-display text-lg uppercase tracking-tight">
                          {it.device_label} <span className="text-ink/40">·</span> <span className="text-accent-bright">{it.issue_label}</span>
                        </p>
                        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                          {new Date(it.created_at).toLocaleString()} · CA ${it.range_low}–${it.range_high} ballpark
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {it.email_sent ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-volt/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-volt">
                          <MailCheck className="h-3.5 w-3.5" /> Emailed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-accent-bright">
                          <MailWarning className="h-3.5 w-3.5" /> Logged only
                        </span>
                      )}
                      <a
                        href={SITE.phoneHref}
                        data-testid={`dashboard-call-${it.id}`}
                        className="inline-flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#EDF1F7] transition-colors hover:bg-volt hover:text-[#0B111B]"
                      >
                        <Phone className="h-3.5 w-3.5" /> Call
                      </a>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}
